import { at, record, values, entries, isArray, put, array, collectWrites } from './support-b1';
import { empty, isset, strval } from '../../php-compat';
import { portWithWrites, type PortWithWrites, type PortWriteIntent, type PortEnv, type PhpRef } from '../../port-contract';
interface Deps {
    creature_statuses(row: unknown, side: string): unknown;
    item_field(row: unknown, key: string): unknown;
    check_uuid(id: unknown): boolean;
    'StatusEffect::getId'(name: string): unknown;
    encounter_creature_field(row: unknown, key: string): unknown;
    search_encounter_arr_for_item_arr(e: unknown, c: unknown, id: unknown): unknown;
    query_d_item_arr(id: unknown, source: string): unknown;
    ft_encounter_context_for_write(row: PhpRef<unknown>): PhpRef<unknown>;
    encounter_read_creature(e: unknown, id: unknown): unknown;
    ft_encounter_roster_row_ref(e: PhpRef<unknown>, id: unknown): PhpRef<unknown>;
    remove_status_effect_status_id_all_connections_to_target(target: PhpRef<unknown>, affected: PhpRef<unknown>, id: unknown, roll: unknown): unknown;
    ft_status_target_unset(row: PhpRef<unknown>, key: unknown): unknown;
}
type PairReadEnv = Pick<PortEnv<Pick<Deps, 'creature_statuses' | 'item_field' | 'check_uuid'
    | 'StatusEffect::getId' | 'encounter_creature_field'>>, 'deps'>;
/** PHP: resolve_grapple_regress_opponent_and_status_row (includes/functions/janus/encounter/grapple_helpers.php:50) */
export function resolveGrappleRegressOpponentAndStatusRow(victim: unknown, itemId: string, opponent: unknown = null,
    env: { deps: Pick<Deps, 'creature_statuses' | 'item_field' | 'check_uuid'> }): unknown {
    if (empty(env.deps.creature_statuses(victim, 'target')) || !isArray(env.deps.creature_statuses(victim, 'target')))
        return null;
    for (const st of values(env.deps.creature_statuses(victim, 'target'))) {
        if ((env.deps.item_field(st, 'item_id') ?? '') !== itemId)
            continue;
        let caster = at(st, 'status_caster_id') ?? '';
        if ((caster === '' || !env.deps.check_uuid(caster)) && !empty(at(st, 'status_origin_id') ?? '')) {
            const origin = at(st, 'status_origin_id') ?? '';
            for (const other of values(env.deps.creature_statuses(victim, 'target'))) {
                if (isset(at(other, 'status_id')) && at(other, 'status_id') === origin || env.deps.item_field(other, 'item_id') !== null && env.deps.item_field(other, 'item_id') === origin) {
                    caster = at(other, 'status_caster_id') ?? caster;
                    break;
                }
            }
        }
        if (caster === '' || !env.deps.check_uuid(caster) || opponent !== null && caster !== opponent)
            continue;
        const id = at(st, 'status_id') ?? '';
        if (id === '' || !env.deps.check_uuid(id))
            continue;
        return record({ restrainer_encounter_creature_id: caster, status_row_id: id });
    }
    return null;
}
/** PHP: resolve_regress_grapple_restrain_restrainer_and_status_row (includes/functions/janus/encounter/grapple_helpers.php:101) */
export function resolveRegressGrappleRestrainRestrainerAndStatusRow(victim: unknown, opponent: unknown = null, env: PortEnv<Deps>): unknown { return resolveGrappleRegressOpponentAndStatusRow(victim, strval(env.deps['StatusEffect::getId']('restrained')), opponent, env); }
/** PHP: resolve_grappled_creature_id_for_grappler_from_encounter (includes/functions/janus/encounter/grapple_helpers.php:132) */
export function resolveGrappledCreatureIdForGrapplerFromEncounter(grappler: unknown, e: unknown, env: PairReadEnv): unknown {
    if (grappler === '' || !env.deps.check_uuid(grappler))
        return null;
    if (!isset(at(e, 'encounter_creatures_arr')) || !isArray(at(e, 'encounter_creatures_arr')))
        return null;
    const id = env.deps['StatusEffect::getId']('grappled');
    for (const row of values(at(e, 'encounter_creatures_arr'))) {
        if (empty(env.deps.creature_statuses(row, 'target')) || !isArray(env.deps.creature_statuses(row, 'target')))
            continue;
        for (const st of values(env.deps.creature_statuses(row, 'target'))) {
            if ((env.deps.item_field(st, 'item_id') ?? at(st, 'status_item_id') ?? '') !== id)
                continue;
            if ((at(st, 'status_caster_id') ?? '') === grappler) {
                const vid = at(row, 'encounter_creatures', 'encounter_creature_id') ?? '';
                if (vid !== '' && env.deps.check_uuid(vid))
                    return vid;
            }
        }
    }
    return null;
}
/** PHP: resolve_release_grapple_target_from_caster_grappling (includes/functions/janus/encounter/grapple_helpers.php:170) */
export function resolveReleaseGrappleTargetFromCasterGrappling(caster: unknown, e: unknown = null, env: PairReadEnv): unknown {
    const grappling = env.deps['StatusEffect::getId']('grappling');
    if (empty(env.deps.creature_statuses(caster, 'target')) || !isArray(env.deps.creature_statuses(caster, 'target')))
        return null;
    const self = at(caster, 'encounter_creatures', 'encounter_creature_id') ?? env.deps.encounter_creature_field(caster, 'encounter_creature_id') ?? '';
    for (const st of values(env.deps.creature_statuses(caster, 'target'))) {
        if ((env.deps.item_field(st, 'item_id') ?? at(st, 'status_item_id') ?? '') !== grappling)
            continue;
        const row = at(st, 'status_id') ?? '';
        if (row === '' || !env.deps.check_uuid(row))
            continue;
        let opponent: unknown = '';
        for (const cid of [at(st, 'status_target_id') ?? '', at(st, 'status_caster_id') ?? '']) {
            if (cid === '' || !env.deps.check_uuid(cid))
                continue;
            if (cid !== self) {
                opponent = cid;
                break;
            }
        }
        if (opponent === '')
            opponent = resolveGrappledCreatureIdForGrapplerFromEncounter(self, e, env) ?? '';
        if (opponent === '')
            continue;
        const connected = at(st, 'status_connected_id') ?? '';
        return record({ grappled_encounter_creature_id: opponent, grappling_status_row_id: row, grapple_status_connected_id: env.deps.check_uuid(connected) ? connected : '' });
    }
    return null;
}
/** PHP: build_regress_grapple_restrain_restrainer_target_arr (includes/functions/janus/encounter/grapple_helpers.php:228) */
export function buildRegressGrappleRestrainRestrainerTargetArr(id: unknown, e: unknown, c: unknown, env: PortEnv<Deps>): unknown {
    if (!env.deps.check_uuid(id))
        return null;
    const row = isset(at(e, 'encounter_creatures_arr')) ? env.deps.search_encounter_arr_for_item_arr(e, c, id) : env.deps.query_d_item_arr(id, 'grapple_contest_or_release opponent creature');
    return empty(row) || !isArray(row) ? null : row;
}
/** PHP: release_grapples_owned_by_dying_grappler (includes/functions/janus/encounter/grapple_helpers.php:277) */
export function releaseGrapplesOwnedByDyingGrappler(dying: PhpRef<unknown>, affected: PhpRef<unknown>, roll: unknown = null, env: PortEnv<Deps>): PortWithWrites<null> {
    const writes: PortWriteIntent[] = [];
    if (empty(env.deps.creature_statuses(dying.value, 'target')) || !isArray(env.deps.creature_statuses(dying.value, 'target')))
        return portWithWrites(null, writes);
    const grappling = env.deps['StatusEffect::getId']('grappling'), grappled = env.deps['StatusEffect::getId']('grappled'), self = at(dying.value, 'encounter_creatures', 'encounter_creature_id') ?? env.deps.encounter_creature_field(dying.value, 'encounter_creature_id') ?? '';
    const encounter = env.deps.ft_encounter_context_for_write(dying);
    const keys = entries(env.deps.creature_statuses(dying.value, 'target')).map(([k]) => k);
    for (const key of keys) {
        const st = at(env.deps.creature_statuses(dying.value, 'target'), key);
        if (st === null)
            continue;
        if ((env.deps.item_field(st, 'item_id') ?? at(st, 'status_item_id') ?? '') !== grappling)
            continue;
        const connected = at(st, 'status_connected_id') ?? '', rowId = at(st, 'status_id') ?? '';
        let victim: unknown = '';
        for (const cid of [at(st, 'status_target_id') ?? '', at(st, 'status_caster_id') ?? '']) {
            if (cid === '' || !env.deps.check_uuid(cid))
                continue;
            if (cid !== self) {
                victim = cid;
                break;
            }
        }
        if (victim === '' && self !== '' && env.deps.check_uuid(self))
            victim = resolveGrappledCreatureIdForGrapplerFromEncounter(self, encounter.value, env) ?? '';
        let found = false, victimStatus: unknown = '';
        if (victim !== '' && isArray(encounter.value)) {
            const snapshot = env.deps.encounter_read_creature(encounter.value, victim);
            if (snapshot !== null && !empty(env.deps.creature_statuses(snapshot, 'target')) && isArray(env.deps.creature_statuses(snapshot, 'target'))) {
                for (const vst of values(env.deps.creature_statuses(snapshot, 'target'))) {
                    if ((env.deps.item_field(vst, 'item_id') ?? at(vst, 'status_item_id') ?? '') !== grappled)
                        continue;
                    const sc = at(vst, 'status_caster_id') ?? '', con = at(vst, 'status_connected_id') ?? '';
                    if (!(sc !== '' && sc === self) && !(connected !== '' && con === connected))
                        continue;
                    const id = at(vst, 'status_id') ?? '';
                    if (id === '' || !env.deps.check_uuid(id))
                        continue;
                    victimStatus = id;
                    break;
                }
            }
        }
        if (victimStatus !== '') {
            const row = env.deps.ft_encounter_roster_row_ref(encounter, victim);
            if (row.value !== null) {
                collectWrites(env.deps.remove_status_effect_status_id_all_connections_to_target(row, affected, victimStatus, roll), writes);
                found = true;
            }
        }
        if (!found && rowId !== '' && env.deps.check_uuid(rowId)) {
            collectWrites(env.deps.remove_status_effect_status_id_all_connections_to_target(dying, affected, rowId, roll), writes);
            continue;
        }
        if (array(env.deps.creature_statuses(dying.value, 'target')).has(key))
            env.deps.ft_status_target_unset(dying, key);
    }
    return portWithWrites(null, writes);
}
